import React from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';

const LocationCard = ({ location, onGetDirections, onCallNow, onViewMenu }) => {
  const getStatusColor = (status) => {
    switch (status) {
      case 'Open':
        return 'bg-[var(--color-trust-green)] text-white';
      case 'Closing Soon':
        return 'bg-[var(--color-warning)] text-[var(--color-warning-foreground)]';
      case 'Closed':
        return 'bg-[var(--color-error)] text-white';
      default:
        return 'bg-[var(--color-muted)] text-[var(--color-muted-foreground)]';
    }
  };

  const getCapacityColor = (percentage) => {
    if (percentage >= 80) return 'text-[var(--color-error)]';
    if (percentage >= 60) return 'text-[var(--color-warning)]';
    return 'text-[var(--color-trust-green)]';
  };

  return (
    <div className="bg-[var(--color-card)] rounded-xl overflow-hidden shadow-md hover:shadow-lg transition-all duration-300 border border-[var(--color-border)]">
      <div className="relative h-48 overflow-hidden">
        <Image
          src={location?.image}
          alt={location?.imageAlt}
          className="w-full h-full object-cover hover:scale-105 transition-transform duration-500"
        />
        <div className="absolute top-4 right-4 flex gap-2">
          <span className={`px-3 py-1 rounded-full text-xs font-semibold ${getStatusColor(location?.status)}`}>
            {location?.status}
          </span>
          {location?.isNew && (
            <span className="px-3 py-1 rounded-full text-xs font-semibold bg-[var(--color-accent)] text-white">
              New
            </span>
          )}
        </div>
      </div>
      <div className="p-6">
        <div className="flex items-start justify-between mb-4">
          <div>
            <h3 className="text-xl font-bold text-[var(--color-foreground)] mb-1">
              {location?.name}
            </h3>
            <p className="text-sm text-[var(--color-muted-foreground)]">
              {location?.area}
            </p>
          </div>
          {location?.rating && (
            <div className="flex items-center gap-1 bg-[var(--color-trust-green)] text-white px-2 py-1 rounded-md">
              <Icon name="Star" size={14} color="#FFFFFF" />
              <span className="text-sm font-semibold">{location?.rating}</span>
            </div>
          )}
        </div>

        <div className="space-y-3 mb-4">
          <div className="flex items-start gap-3">
            <Icon name="MapPin" size={18} color="var(--color-primary)" className="mt-0.5 flex-shrink-0" />
            <p className="text-sm text-[var(--color-foreground)]">{location?.address}</p>
          </div>

          <div className="flex items-center gap-3">
            <Icon name="Clock" size={18} color="var(--color-primary)" className="flex-shrink-0" />
            <p className="text-sm text-[var(--color-foreground)]">{location?.hours}</p>
          </div>

          <div className="flex items-center gap-3">
            <Icon name="Phone" size={18} color="var(--color-primary)" className="flex-shrink-0" />
            <p className="text-sm text-[var(--color-foreground)]">{location?.phone}</p>
          </div>

          {location?.waitTime && (
            <div className="flex items-center gap-3">
              <Icon name="Timer" size={18} color="var(--color-primary)" className="flex-shrink-0" />
              <p className="text-sm text-[var(--color-foreground)]">
                Wait Time: <span className="font-semibold">{location?.waitTime}</span>
              </p>
            </div>
          )}

          {location?.capacity && (
            <div className="flex items-center gap-3">
              <Icon name="Users" size={18} color="var(--color-primary)" className="flex-shrink-0" />
              <div className="flex-1">
                <div className="flex items-center justify-between mb-1">
                  <p className="text-sm text-[var(--color-foreground)]">Capacity</p>
                  <span className={`text-sm font-semibold ${getCapacityColor(location?.capacity)}`}>
                    {location?.capacity}%
                  </span>
                </div>
                <div className="w-full bg-[var(--color-muted)] rounded-full h-2">
                  <div
                    className="h-2 rounded-full transition-all duration-300"
                    style={{
                      width: `${location?.capacity}%`,
                      backgroundColor: location?.capacity >= 80 ? 'var(--color-error)' : location?.capacity >= 60 ? 'var(--color-warning)' : 'var(--color-trust-green)'
                    }}
                  />
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="flex flex-wrap gap-2 mb-4">
          {location?.amenities?.map((amenity, index) => (
            <span
              key={index}
              className="inline-flex items-center gap-1 px-3 py-1 bg-[var(--color-primary)]/10 text-[var(--color-primary)] rounded-full text-xs font-medium"
            >
              <Icon name={amenity?.icon} size={12} />
              {amenity?.label}
            </span>
          ))}
        </div>

        {location?.parking && (
          <div className="flex items-center gap-2 mb-4 p-3 bg-[var(--color-muted)] rounded-lg">
            <Icon name="ParkingCircle" size={18} color="var(--color-primary)" />
            <p className="text-sm text-[var(--color-foreground)]">{location?.parking}</p>
          </div>
        )}

        <div className="grid grid-cols-2 gap-3">
          <Button
            variant="default"
            size="default"
            iconName="Navigation"
            iconPosition="left"
            onClick={() => onGetDirections(location)}
            fullWidth
          >
            Directions
          </Button>
          <Button
            variant="outline"
            size="default"
            iconName="Phone"
            iconPosition="left"
            onClick={() => onCallNow(location)}
            fullWidth
          >
            Call Now
          </Button>
        </div>

        <Button
          variant="secondary"
          size="default"
          iconName="Menu"
          iconPosition="left"
          onClick={() => onViewMenu(location)}
          fullWidth
          className="mt-3"
        >
          View Menu
        </Button>
      </div>
    </div>
  );
};

export default LocationCard;